import { css, cx } from '@linaria/core'
import { type FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { store, useAppSelector } from '#app/store'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import { Button } from '#components/Button'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { Modal } from '#components/Modal'
import { Spinner } from '#components/Spinner'
import ExitIcon from '#icons/exit-24.svg'
import { configurationStatus } from '#pages/Configuration/components/configurationStatus'
import { fragments, style } from '#styles/index'
import { expandBackUrl } from '#utils/expandBackUrl'

const callingSystemExitClass = css`
  display: flex;
  align-items: center;
  // the icon has some offset to the bottom, pushing it more in line
  margin-top: 4px;
  height: 24px;
  cursor: pointer;

  &.disabled {
    color: ${style.themeColors.textDisabled};
    cursor: not-allowed;
  }
`

const modalClass = css`
  min-width: 450px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  > h1 {
    ${fragments.textStyle.heading.l.regular}
  }

  > div > ul {
    list-style: none;
    > li {
      padding: 4px 0;
    }
  }

  > .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
`

const getConfigurationsByStatus = (configurations: (TruckConfiguration | undefined)[]) => {
  const byStatus: Record<ReturnType<typeof configurationStatus>, TruckConfiguration[]> = {
    pending: [],
    unsaved: [],
    error: [],
    'read-only': [],
    saved: [],
  }
  for (const configuration of configurations) {
    if (!configuration) {
      continue
    }
    const status = configurationStatus(configuration)
    byStatus[status].push(configuration)
  }
  return byStatus
}

const useConfigurationsByStatus = () => {
  const configurations = useAppSelector((x) => x.truckConfigurations.configurations)
  return useMemo(() => getConfigurationsByStatus(Object.values(configurations)), [configurations])
}

const PendingExitModal: FC<{ onOpenChange: (open: boolean) => void; exitUrl: string | undefined }> = ({ onOpenChange, exitUrl }) => {
  const configurations = useConfigurationsByStatus()
  const { t } = useTranslation()

  return (
    <Modal open shadow lifted contentClassName={modalClass} onOpenChange={onOpenChange}>
      <h1>{t('app.header.exit.pending.title', 'Exiting with pending changes')}</h1>
      {configurations.pending.length > 0 && (
        <div>
          {t('app.header.exit.pending.description', 'The following configurations are still saving. You can exit after they have been saved:')}
          <ul>
            {configurations.pending.map((x) => (
              <li key={x.id}>
                {x.marketDenomination} ({x.apiConfiguration.partPeriod})
              </li>
            ))}
          </ul>
        </div>
      )}
      {<div>{t('app.header.exit.pending.success', 'All pending configurations have been saved. It is now safe to exit the application.')}</div>}
      <div className="actions">
        <Button kind="secondary" onClick={() => onOpenChange(false)}>
          {t('shared:common.button.cancel')}
        </Button>
        <Button
          disabled={configurations.pending.length > 0}
          onClick={() => {
            if (exitUrl) {
              window.location.href = exitUrl
            }
          }}
        >
          {configurations.pending.length > 0 && <Spinner size="xs" />}
          {t('app.header.exit.action.exit', 'Exit to Calling System')}
        </Button>
      </div>
    </Modal>
  )
}

const UnsavedExitModal: FC<{ onOpenChange: (open: boolean) => void; exitUrl: string | undefined }> = ({ onOpenChange, exitUrl }) => {
  const configurations = useConfigurationsByStatus()
  const { t } = useTranslation()

  return (
    <Modal open shadow lifted contentClassName={modalClass} onOpenChange={onOpenChange}>
      <h1>{t('app.header.exit.unsaved.title', 'Exiting with unsaved changes')}</h1>
      <div>
        {t('app.header.exit.unsaved.description', "You're about to exit a session while some configurations are unsaved")}
        <ul>
          {configurations.unsaved.map((x) => (
            <li key={x.id}>
              {x.marketDenomination} ({x.apiConfiguration.partPeriod})
            </li>
          ))}
        </ul>
      </div>
      <div className="actions">
        <Button kind="secondary" onClick={() => onOpenChange(false)}>
          {t('shared:common.button.cancel')}
        </Button>
        <Button
          onClick={() => {
            if (exitUrl) {
              window.location.href = exitUrl
            }
          }}
        >
          {t('app.header.exit.action.exit', 'Exit to Calling System')}
        </Button>
      </div>
    </Modal>
  )
}

export const CallingSystemExit: FC = () => {
  const { t } = useTranslation()
  const backUrl = useAppSelector((x) => x.truckConfigurations.apiConfiguration.backUrl)
  const hasBackUrl = !!backUrl
  const tooltip = hasBackUrl ? t('app.header.exit.available', 'Exit to calling system') : t('app.header.exit.unavailable', 'System exit is disabled')

  const [awaitReason, setAwaitReason] = useState<'pending' | 'unsaved' | undefined>(undefined)
  const finalBackUrl = useMemo(() => expandBackUrl(backUrl ?? '/setup', 'unknown', 'done', 'UNKNOWN'), [backUrl])

  const exitClick = useCallback(() => {
    const state = store.getState()
    const byStatus = getConfigurationsByStatus(Object.values(state.truckConfigurations.configurations))

    if (byStatus.unsaved.length > 0) {
      setAwaitReason('unsaved')
      return
    }
    if (byStatus.pending.length > 0) {
      setAwaitReason('pending')
      return
    }

    window.location.href = finalBackUrl
  }, [finalBackUrl])

  return (
    <HoverTooltip.div title={tooltip}>
      {awaitReason === 'unsaved' && <UnsavedExitModal onOpenChange={() => setAwaitReason(undefined)} exitUrl={finalBackUrl} />}
      {awaitReason === 'pending' && <PendingExitModal onOpenChange={() => setAwaitReason(undefined)} exitUrl={finalBackUrl} />}
      <Icon className={cx(callingSystemExitClass, !backUrl && 'disabled')} iconUrl={ExitIcon} size="s" onClick={exitClick} />
    </HoverTooltip.div>
  )
}
